/**
 * Renders Okendo reviews in the theme's own markup.
 *
 * Okendo's Storefront REST API is public and unauthenticated — it is keyed by
 * the shop's subscriber id, the same one their widget uses client-side — and
 * sends Access-Control-Allow-Origin: *, so the storefront can call it directly.
 *
 *   GET /v1/stores/{subscriberId}/reviews
 *   GET /v1/stores/{subscriberId}/products/shopify-{productId}/reviews
 *
 * `limit` is capped at 25 by Okendo, so more than that is paged through the
 * `nextUrl` the response carries, bounded by the section's max-pages setting.
 */
if (!customElements.get('okendo-reviews')) {
  const API_ORIGIN = 'https://api.okendo.io';

  class OkendoReviews extends HTMLElement {
    connectedCallback() {
      if (this.loaded) return;
      this.loaded = true;

      this.list = this.querySelector('[data-list]');
      this.summary = this.querySelector('[data-summary]');
      this.status = this.querySelector('[data-status]');
      this.actions = this.querySelector('[data-actions]');
      this.loadMoreButton = this.querySelector('[data-load-more]');

      this.reviews = this.readSeedReviews();
      this.nextUrl = null;
      this.pagesLoaded = 0;
      this.maxPages = parseInt(this.dataset.maxPages, 10) || 4;
      this.pageSize = parseInt(this.dataset.pageSize, 10) || 25;
      this.showProduct = this.dataset.showProduct === 'true';

      this.loadMoreButton?.addEventListener('click', () => this.loadPage());

      // The store's reviews are already here, but rendering them now would
      // show a short list that then reflows as Okendo's arrive. Hold both
      // until the first fetch resolves so the section appears once, complete.
      // If that fetch fails, loadPage renders what we already have.
      this.loadPage();
    }

    /**
     * Reviews held as `theme_review` metaobjects, rendered into the page by
     * the section in Okendo's shape so both sources merge without branching.
     */
    readSeedReviews() {
      const node = this.querySelector('[data-seed-reviews]');
      if (!node) return [];

      try {
        const parsed = JSON.parse(node.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn('[okendo-reviews] could not parse store reviews', error);
        return [];
      }
    }

    get firstUrl() {
      const base = `${API_ORIGIN}/v1/stores/${this.dataset.subscriberId}`;
      const path =
        this.dataset.scope === 'product' && this.dataset.productId
          ? `${base}/products/shopify-${this.dataset.productId}/reviews`
          : `${base}/reviews`;

      return `${path}?limit=${this.pageSize}&orderBy=date%20desc`;
    }

    async loadPage() {
      const url = this.nextUrl ? `${API_ORIGIN}/v1${this.nextUrl}` : this.firstUrl;

      this.setBusy(true);

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Okendo responded ${response.status}`);

        const data = await response.json();
        this.reviews = this.sortByDate(this.reviews.concat(data.reviews || []));
        this.nextUrl = data.nextUrl || null;
        this.pagesLoaded += 1;

        this.render();
      } catch (error) {
        // A reviews section is not worth breaking the page over; leave the
        // status line in place with a neutral message.
        console.warn('[okendo-reviews]', error);

        if (this.reviews.length) {
          // Okendo is unreachable but the store's own reviews are already here.
          this.render();
        } else {
          this.status.textContent = window.okendoStrings?.error || 'Reviews are unavailable right now.';
          this.status.hidden = false;
          this.actions.hidden = true;
        }
      } finally {
        this.setBusy(false);
      }
    }

    sortByDate(reviews) {
      return reviews
        .slice()
        .sort((a, b) => new Date(b.dateCreated || 0) - new Date(a.dateCreated || 0));
    }

    setBusy(isBusy) {
      this.toggleAttribute('aria-busy', isBusy);
      if (this.loadMoreButton) this.loadMoreButton.disabled = isBusy;
    }

    render() {
      if (!this.reviews.length) {
        this.status.textContent = window.okendoStrings?.empty || 'No reviews yet.';
        this.status.hidden = false;
        this.actions.hidden = true;
        return;
      }

      this.status.hidden = true;
      this.renderSummary();

      this.list.innerHTML = '';
      this.reviews.forEach((review) => this.list.appendChild(this.buildCard(review)));

      const canLoadMore = Boolean(this.nextUrl) && this.pagesLoaded < this.maxPages;
      this.actions.hidden = !canLoadMore;
    }

    renderSummary() {
      const total = this.reviews.length;
      const average = this.reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / total;

      this.summary.innerHTML = '';
      this.summary.appendChild(this.buildStars(average));

      const value = document.createElement('span');
      value.className = 'okendo-reviews__summary-value';
      value.textContent = `${average.toFixed(2).replace(/\.?0+$/, '')} out of 5`;

      const count = document.createElement('span');
      count.className = 'okendo-reviews__summary-count';
      // Only exact while every page has been fetched; say "of" once it is not.
      count.textContent = this.nextUrl
        ? `Based on the ${total} most recent reviews`
        : `Based on ${total} review${total === 1 ? '' : 's'}`;

      this.summary.append(value, count);

      if (this.dataset.writeReviewUrl) {
        const link = document.createElement('a');
        link.className = 'okendo-reviews__write';
        link.href = this.dataset.writeReviewUrl;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = window.okendoStrings?.write || 'Write a review';
        this.summary.appendChild(link);
      }

      this.summary.hidden = false;
    }

    buildStars(rating) {
      const wrapper = document.createElement('div');
      wrapper.className = 'okendo-stars';
      wrapper.setAttribute('role', 'img');
      wrapper.setAttribute('aria-label', `Rated ${Number(rating).toFixed(1)} out of 5`);

      for (let i = 1; i <= 5; i += 1) {
        const star = document.createElement('span');
        star.className = 'okendo-stars__star';
        star.setAttribute('aria-hidden', 'true');
        if (rating >= i - 0.25) star.classList.add('is-filled');
        star.textContent = '★';
        wrapper.appendChild(star);
      }

      return wrapper;
    }

    buildCard(review) {
      const card = document.createElement('article');
      card.className = 'okendo-review';

      const top = document.createElement('header');
      top.className = 'okendo-review__top';
      top.appendChild(this.buildStars(review.rating));

      if (review.dateCreated) {
        const date = document.createElement('time');
        date.className = 'okendo-review__date';
        date.dateTime = review.dateCreated;
        date.textContent = new Date(review.dateCreated).toLocaleDateString(undefined, {
          year: '2-digit',
          month: '2-digit',
          day: '2-digit'
        });
        top.appendChild(date);
      }
      card.appendChild(top);

      const author = document.createElement('div');
      author.className = 'okendo-review__author';

      const name = document.createElement('span');
      name.className = 'okendo-review__name';
      name.textContent = review.reviewer?.displayName || 'Anonymous';
      author.appendChild(name);

      if (review.reviewer?.isVerified) {
        const badge = document.createElement('span');
        badge.className = 'okendo-review__verified';
        badge.textContent = window.okendoStrings?.verified || 'Verified';
        author.appendChild(badge);
      }
      card.appendChild(author);

      if (review.title) {
        const title = document.createElement('h3');
        title.className = 'okendo-review__title';
        title.textContent = review.title.trim();
        card.appendChild(title);
      }

      if (review.body) {
        const body = document.createElement('p');
        body.className = 'okendo-review__body';
        body.textContent = review.body.trim();
        card.appendChild(body);
      }

      // Store-wide reviews are shown on every product, so each card has to say
      // which product it is actually about.
      if (this.showProduct && review.productName) {
        const product = document.createElement('p');
        product.className = 'okendo-review__product';

        if (review.productUrl) {
          const link = document.createElement('a');
          link.href = review.productUrl.startsWith('//')
            ? `https:${review.productUrl}`
            : review.productUrl;
          link.textContent = review.productName;
          product.appendChild(link);
        } else {
          product.textContent = review.productName;
        }

        card.appendChild(product);
      }

      return card;
    }
  }

  customElements.define('okendo-reviews', OkendoReviews);
}
